import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import { useTranslation } from 'react-i18next'
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams
} from 'react-router-dom'
import { Container, TopBar } from 'components'
import { buildClientUrl, constants, getConnectUser } from 'helpers'
import { useBindDispatch, useGetClient } from 'hooks'
import { useSelector } from 'react-redux'
import { buildPaymentSummaryProps } from './helpers/buildPaymentSummaryProps'
import { PaymentLoadingSection } from './components/PaymentLoadingSection'
import { PaymentHistoryContent } from './components/PaymentHistoryContent'
import { useGetClientPayments } from './hooks/useGetClientPayments'
import { getClientTransactionDetails } from './helpers/getClientTransactionData'
import { processPaymentsUpdate } from './helpers/processClientTransactionHelpers'
import { isAnyDelivered } from 'helpers/isAnyDelivered'

const EMPTY_PAYMENTS = []

export const ClientPaymentHistory = () => {
    const urlStateParam = useLocation()

    const fromURL = urlStateParam?.state?.from

    const [animate, setAnimate] = useState(false)
    const { villageId: urlVillageId, clientId: urlClientCode } = useParams()
    const { transactions } = useSelector(
        (state) => state.transactions[urlVillageId] || {}
    )
    const { clientOrders } = useSelector((state) => state.clientOrders)
    const [searchParams] = useSearchParams()
    const reload = searchParams.get('reload')
    const nodeRef = useRef(null)
    const { t } = useTranslation()
    const {
        navbarHandler,
        updateSingleClientBalAndSmsHandler,
        transactionsHandler,
        getClientOrdersHandler
    } = useBindDispatch()
    const { country } = getConnectUser()
    const navigate = useNavigate()
    const { client, loadingClient } = useGetClient(urlVillageId, urlClientCode)
    const clientTransactionsData = useMemo(
        () => client && getClientTransactionDetails(client, transactions),
        [client, transactions?.length]
    )

    const clientPayments = useGetClientPayments(EMPTY_PAYMENTS, client?.client_code)

    const processedPayments = useMemo(
        () =>
            processPaymentsUpdate(
                clientPayments,
                clientTransactionsData?.transactionList,
                country
            ),
        [
            clientPayments?.length,
            clientTransactionsData?.transactionList?.length,
            country
        ]
    )

    useEffect(() => {
        getClientOrdersHandler(urlClientCode, urlVillageId)
    }, [])

    useEffect(() => {
        setAnimate(true)
        navbarHandler(constants.SHOW_NAVBAR)
        if (reload) {
            updateSingleClientBalAndSmsHandler({
                villageId: urlVillageId,
                clientId: urlClientCode
            })
        }
    }, [reload])

    useEffect(() => {
        transactionsHandler({ villageId: urlVillageId })
    }, [])

    const backNavigationHandler = () => {
        navigate(
            buildClientUrl(client),
            fromURL ? { state: { from: fromURL } } : undefined
        )
    }

    const { fullname, all_enrollment: allEnrollment } = client || {}

    const validatedLastVoucherDate = clientTransactionsData?.lastPaymentDate
        ? t('fullDateMonthYear', {
              date: new Date(clientTransactionsData?.lastPaymentDate)
          })
        : '-'

    return (
        <>
            <div className="sticky-header" id="sticky-header">
                <Container>
                    <TopBar back backNavigation={backNavigationHandler}>
                        <span className="client-payment-history--header">
                            {fullname}
                        </span>
                    </TopBar>
                </Container>
            </div>
            {loadingClient ? (
                <PaymentLoadingSection
                    loadingText={t('fetchingPaymentLoader')}
                />
            ) : (
                <CSSTransition
                    unmountOnExit
                    timeout={constants.ANIMATION_TIMEOUT}
                    in={animate}
                    classNames="generic"
                    appear
                    onEnter={() => setAnimate(true)}
                    nodeRef={nodeRef}
                >
                    <PaymentHistoryContent
                        paymentSummary={buildPaymentSummaryProps(
                            {
                                allEnrollment,
                                balance:
                                    clientTransactionsData?.clientBalance || 0
                            },
                            validatedLastVoucherDate
                        )}
                        isAnyDelivered={isAnyDelivered(clientOrders)}
                        transactions={Object.entries(processedPayments)}
                        country={country}
                        animate={animate}
                    />
                </CSSTransition>
            )}
        </>
    )
}
